import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Category } from '../../types/category';

const categorySchema = z.object({
  name: z
    .string()
    .min(3, 'Category name must be at least 3 characters')
    .max(50, 'Category name must be less than 50 characters')
    .regex(/^[a-zA-Z\s-]+$/, 'Category name can only contain letters, spaces, and hyphens'),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CategoryFormData) => Promise<void>;
  category?: Category;
  isLoading?: boolean;
}

export function CategoryModal({
  isOpen,
  onClose,
  onSubmit,
  category,
  isLoading,
}: CategoryModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: category?.name || '',
    },
  });

  // Reset form when modal opens/closes or category changes
  React.useEffect(() => {
    if (isOpen) {
      reset({ name: category?.name || '' });
    }
  }, [isOpen, category, reset]);

  const handleFormSubmit = async (data: CategoryFormData) => {
    try {
      await onSubmit(data);
      reset();
    } catch (error) {
      // Error will be handled by the parent component
      throw error;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      title={category ? 'Edit Category' : 'Add New Category'}
    >
      <div className="space-y-4">
        <Input
          label="Category Name"
          placeholder="Enter category name"
          {...register('name')}
          error={errors.name?.message}
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            type="button" 
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isLoading}
          >
            {category ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}